import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { getColors, getDynamicBaseStyles } from '@/lib/theme'
import { getSelectedThemeId } from '@/types/UserSettings'

type ThemeColors = ReturnType<typeof getColors>

interface ThemeContextValue {
  isDark: boolean
  colors: ThemeColors
  toggleTheme: () => void
  setIsDark: (value: boolean) => void
}

const STORAGE_KEY = 'kyogre-theme-mode'

const ThemeContext = createContext<ThemeContextValue | null>(null)

interface ThemeProviderProps {
  children: React.ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Initialize state from DOM (set by inline script) to avoid flash
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      // Check if inline script already applied dark class
      return document.documentElement.classList.contains('dark')
    }
    return false
  })
  const [themeVersion, setThemeVersion] = useState(0)
  const [customThemeLoaded, setCustomThemeLoaded] = useState(false)

  // Apply dynamic styles and load custom theme (consolidated effect)
  useEffect(() => {
    const applyThemeStyles = () => {
      // Persist theme mode to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light')
      } catch (error) {
        console.warn('Unable to persist theme mode:', error)
      }

      // Ensure DOM class is in sync (redundant on initial load but needed for toggles)
      document.documentElement.classList.toggle('dark', isDark)

      // Apply base dynamic styles and CSS variables
      try {
        const colors = getColors(isDark)
        const dynamicStyles = getDynamicBaseStyles(isDark)

        // Apply dynamic styles
        for (const [selector, styles] of Object.entries(dynamicStyles)) {
          const element =
            selector === 'body'
              ? document.body
              : document.querySelector(selector)
          if (element) Object.assign((element as HTMLElement).style, styles)
        }

        // Apply CSS variables for mobile nav
        document.documentElement.style.setProperty(
          '--mobile-nav-bg',
          colors.mobileNavBg
        )
        document.documentElement.style.setProperty(
          '--mobile-nav-border',
          colors.mobileNavBorder
        )
        document.documentElement.style.setProperty(
          '--mobile-nav-icon-hover',
          colors.mobileNavIconHover
        )
        document.documentElement.style.setProperty(
          '--mobile-nav-icon-active',
          colors.mobileNavIconActive
        )

        // Apply scrollbar CSS variables
        document.documentElement.style.setProperty(
          '--scrollbar-thumb',
          colors.scrollbarThumb
        )
        document.documentElement.style.setProperty(
          '--scrollbar-track',
          colors.scrollbarTrack
        )
      } catch {}
    }

    // Apply styles immediately
    applyThemeStyles()

    // Custom theme is already applied by inline script, just need to update internal state
    if (!customThemeLoaded) {
      const savedThemeId = getSelectedThemeId()
      if (savedThemeId) {
        // Theme colors already applied by inline script, just update the theme object
        import('@/lib/theme').then((m) => {
          // Check if we have cached theme to apply to internal state
          import('@/types/UserSettings').then((us) => {
            const cachedTheme = us.getCachedThemeJson()
            if (cachedTheme) {
              // Apply to internal theme object (CSS vars already set by inline script)
              m.applyThemeGlobally(cachedTheme)
            }
            setCustomThemeLoaded(true)
          })
        })
      } else {
        setCustomThemeLoaded(true)
      }
    }
  }, [isDark, customThemeLoaded])

  // Handle runtime theme updates (when custom themes are applied)
  useEffect(() => {
    const onUpdate = () => {
      setThemeVersion((v) => v + 1)
      // Re-apply base styles and CSS variables with current theme colors
      try {
        const colors = getColors(isDark)
        const dynamicStyles = getDynamicBaseStyles(isDark)

        for (const [selector, styles] of Object.entries(dynamicStyles)) {
          const element =
            selector === 'body'
              ? document.body
              : document.querySelector(selector)
          if (element) Object.assign((element as HTMLElement).style, styles)
        }

        // Re-apply CSS variables for mobile nav
        document.documentElement.style.setProperty(
          '--mobile-nav-bg',
          colors.mobileNavBg
        )
        document.documentElement.style.setProperty(
          '--mobile-nav-border',
          colors.mobileNavBorder
        )
        document.documentElement.style.setProperty(
          '--mobile-nav-icon-hover',
          colors.mobileNavIconHover
        )
        document.documentElement.style.setProperty(
          '--mobile-nav-icon-active',
          colors.mobileNavIconActive
        )

        // Re-apply scrollbar CSS variables
        document.documentElement.style.setProperty(
          '--scrollbar-thumb',
          colors.scrollbarThumb
        )
        document.documentElement.style.setProperty(
          '--scrollbar-track',
          colors.scrollbarTrack
        )
      } catch {}
    }

    window.addEventListener('kyogre-theme-updated', onUpdate as EventListener)
    return () => {
      window.removeEventListener(
        'kyogre-theme-updated',
        onUpdate as EventListener
      )
    }
  }, [isDark])

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => !prev)
  }, [])

  const value = useMemo<ThemeContextValue>(() => {
    // Spread to create a new object reference so React detects changes
    const colors = { ...getColors(isDark) }
    return {
      isDark,
      colors,
      toggleTheme,
      setIsDark,
    }
  }, [isDark, themeVersion, toggleTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useThemeMode(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) {
    const colors = getColors(false)
    return {
      isDark: false,
      colors,
      toggleTheme: () => {
        console.warn(
          'ThemeProvider is missing in component tree. Theme toggle is a no-op.'
        )
      },
      setIsDark: () => {
        console.warn(
          'ThemeProvider is missing in component tree. setIsDark is a no-op.'
        )
      },
    }
  }
  return context
}

export function useThemeColors(): ThemeColors {
  return useThemeMode().colors
}
